import { useMutateCreateAndLinkPatientProfile } from '@/hooks/mutation/useMutateCreatePatientProfile';
import { useMutateLinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateLinkSessionsToPatientProfile';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { useCallback, useMemo } from 'react';

import { type GdtPatientData } from '@heidi/integrations/ehr/contracts';
import { convertGdtToPatientProfile } from '../utils';

/**
 * Hook result for creating GDT patient profiles
 */
export interface UseCreateGdtPatientProfileResult {
  /**
   * Create or find a patient profile from GDT data and link it to sessions
   *
   * If a patient with the same ehr_patient_id already exists, the session
   * will be linked to that existing patient instead of creating a duplicate.
   */
  createOrLinkGdtPatient: (
    gdtPatient: GdtPatientData,
    sessionIds: string[],
    ehrProvider?: EHRProvider
  ) => Promise<{ isExisting: boolean }>;
  /** Whether the mutation is in progress */
  isLoading: boolean;
  /**
   * @deprecated Use createOrLinkGdtPatient instead
   */
  createAndLinkGdtPatient: (
    gdtPatient: GdtPatientData,
    sessionIds: string[]
  ) => Promise<void>;
}

/**
 * Hook to create or find a PatientProfile from GDT data and link it to sessions
 *
 * When a patient is launched from a PVS via GDT, this hook:
 * 1. Checks if a patient with the same ehr_patient_id already exists
 * 2. If exists: Links the session(s) to the existing patient
 * 3. If not exists: Creates a new PatientProfile and links session(s)
 *
 * The GDT patientId is stored as ehr_patient_id for future matching.
 *
 * @example
 * ```tsx
 * function GdtSessionHandler() {
 *   const { patient } = useGdtPatient();
 *   const sessionId = useCurrentSessionId();
 *   const { createOrLinkGdtPatient, isLoading } = useCreateGdtPatientProfile();
 *
 *   useEffect(() => {
 *     if (patient && sessionId) {
 *       createOrLinkGdtPatient(patient, [sessionId])
 *         .then(({ isExisting }) => {
 *           if (isExisting) {
 *             console.log('Linked to existing patient');
 *           } else {
 *             console.log('Created new patient');
 *           }
 *         });
 *     }
 *   }, [patient, sessionId]);
 *
 *   return isLoading ? <Spinner /> : null;
 * }
 * ```
 */
export function useCreateGdtPatientProfile(): UseCreateGdtPatientProfileResult {
  const createMutation = useMutateCreateAndLinkPatientProfile();
  const linkMutation = useMutateLinkSessionsToPatientProfile();
  const { data: existingPatients } = useQueryPatientProfiles();

  /**
   * Find an existing patient profile by ehr_patient_id
   */
  const findExistingPatient = useCallback(
    (ehrPatientId: string) => {
      if (!existingPatients) return null;

      return (
        existingPatients.find((profile) => profile.ehr_patient_id === ehrPatientId) ??
        null
      );
    },
    [existingPatients]
  );

  const createOrLinkGdtPatient = useCallback(
    async (
      gdtPatient: GdtPatientData,
      sessionIds: string[],
      ehrProvider?: EHRProvider
    ) => {
      // Check for existing patient with same ehr_patient_id
      const existingPatient = findExistingPatient(gdtPatient.patientId);

      if (existingPatient) {
        // Patient already exists - just link the sessions
        await linkMutation.mutateAsync({
          patientProfile: existingPatient,
          sessionIds,
        });
        return { isExisting: true };
      }

      // No existing patient found - create new one
      const patientProfile = convertGdtToPatientProfile(gdtPatient, ehrProvider);
      await createMutation.mutateAsync({
        patientProfile,
        sessionIds,
      });
      return { isExisting: false };
    },
    [findExistingPatient, linkMutation, createMutation]
  );

  // Keep deprecated method for backwards compatibility
  const createAndLinkGdtPatient = useCallback(
    async (gdtPatient: GdtPatientData, sessionIds: string[]) => {
      await createOrLinkGdtPatient(gdtPatient, sessionIds);
    },
    [createOrLinkGdtPatient]
  );

  const isLoading = useMemo(
    () => createMutation.isPending || linkMutation.isPending,
    [createMutation.isPending, linkMutation.isPending]
  );

  return {
    createOrLinkGdtPatient,
    createAndLinkGdtPatient,
    isLoading,
  };
}
